import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChannelIcon } from '@/components/channels/ChannelIcon';
import { DisableChannelDialog } from '@/components/channels/DisableChannelDialog';
import { NotFound } from '@/pages/errors/NotFound';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useGoBack } from '@/hooks/useGoBack';
import { useChannels } from '@/hooks/useChannels';
import { useChannelActions } from '@/hooks/useChannelActions';
import { useChannelDefinitions } from '@/hooks/useChannelDefinitions';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { buildChannel, hasChannelSettingsPage, isOrderChannel } from '@/lib/channels';

import { MultiItemBasketCard } from './settings/MultiItemBasketCard';
import { NotificationEmailsCard } from './settings/NotificationEmailsCard';
import { SmsNotificationsCard, hasExistingSmsConfig } from './settings/SmsNotificationsCard';
import { EmailTemplatesCard } from './settings/EmailTemplatesCard';

function SettingsLoading() {
	return (
		<div className="space-y-8">
			<Skeleton className="h-4 w-32" />
			<Skeleton className="h-12 w-72" />
			<Skeleton className="h-48" />
			<Skeleton className="h-64" />
		</div>
	);
}

/**
 * Channel settings sub-page, reachable from the ChannelManage sidebar's
 * "Channel settings" pill. Every channel a retailer can switch off has one
 * (`hasChannelSettingsPage`), and it owns that channel's Disable action —
 * moved off the manage-page sidebar in NEA-6537.
 *
 * The order-channel pair (Product Reservations and Local Checkout) also get
 * the notification-email card (shared across both channels), the SMS card
 * (Local Checkout only and gated to retailers who already have SMS
 * configured — v1 of NEA-6015) and the per-channel email-template editor.
 * Other channels show only the Disable card for now.
 *
 * The settings are intentionally shop- and channel-scoped; data lives on
 * the `channels-{stage}` record under `config.{notificationEmails,
 * notificationPhoneNumbers, smsEnabled, multiItemBasket, templates}` and the writes go
 * through the existing `PATCH /api/channels/{shopId}/{type}/update`
 * endpoint (`org:channels:manage`) — no new API surface needed.
 */
export function ChannelSettings() {
	const { id = '' } = useParams();
	const navigate = useNavigate();
	const { shop } = useShop();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { definitions, isLoading: defsLoading } = useChannelDefinitions();
	const { disable, isBusy } = useChannelActions(shop?.id);
	const { billing } = useBillingStatus(shop?.id);
	const { shopPath } = useShopPath();
	const goBack = useGoBack(`/channels/${id}/manage`);
	const [confirmingDisable, setConfirmingDisable] = useState(false);

	if (!hasChannelSettingsPage(id)) return <NotFound />;
	if (!shop?.id || channelsLoading || defsLoading) return <SettingsLoading />;

	const channel = buildChannel(id, definitions, channels, billing?.entitlements, shop?.currency);
	if (!channel) return <NotFound />;
	// Settings only make sense for a live channel — otherwise send the
	// retailer to the detail / setup page where they can switch it on.
	if (!channel.live) return <Navigate to={shopPath(`/channels/${id}`)} replace />;

	const localCheckoutChannel = channels.find((c) => c.type === 'local-checkout');
	const currentChannel = channels.find((c) => c.type === id);
	const notificationEmails = currentChannel?.config?.notificationEmails || [];
	const phoneNumbers = localCheckoutChannel?.config?.notificationPhoneNumbers || [];
	const smsEnabled = Boolean(localCheckoutChannel?.config?.smsEnabled);
	const showSms = id === 'local-checkout' && hasExistingSmsConfig(localCheckoutChannel);
	const busy = isBusy(channel.id);
	const orderChannel = isOrderChannel(id);

	return (
		<div className="space-y-8">
			<button
				type="button"
				onClick={goBack}
				className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
			>
				<ArrowLeft className="h-3 w-3" /> Back
			</button>

			<header className="flex flex-wrap items-start gap-4">
				<ChannelIcon channelId={channel.id} src={channel.iconUrl} className="h-12 w-12" />
				<div className="min-w-0 flex-1">
					<p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{channel.title}</p>
					<h1 className="mt-1 text-2xl font-extrabold tracking-tight md:text-3xl">Channel settings</h1>
					<p className="mt-1.5 max-w-3xl text-base text-muted-foreground">
						{orderChannel
							? 'Choose how you want to be notified about new orders and tailor the emails customers receive.'
							: `Manage how ${channel.title} works for your store.`}
					</p>
				</div>
			</header>

			<div className="space-y-6">
				{orderChannel ? (
					<>
						<MultiItemBasketCard
							key={id}
							shopId={shop.id}
							channelType={id}
							value={Boolean(currentChannel?.config?.multiItemBasket)}
						/>
						<NotificationEmailsCard shopId={shop.id} channels={channels} value={notificationEmails} />
						{showSms ? (
							<SmsNotificationsCard
								shopId={shop.id}
								channels={channels}
								phoneNumbers={phoneNumbers}
								smsEnabled={smsEnabled}
							/>
						) : null}
						<EmailTemplatesCard shop={shop} channelType={id} channels={channels} />
					</>
				) : null}

				<SoftCard className="flex flex-wrap items-center justify-between gap-4 !p-5 md:!p-6">
					<div className="min-w-0">
						<h3 className="text-[15px] font-bold text-foreground md:text-base">Disable {channel.title}</h3>
						<p className="mt-0.5 text-xs text-muted-foreground">
							Stop sending your products to {channel.title}. You can switch it back on at any time.
						</p>
					</div>
					<button
						type="button"
						disabled={busy}
						onClick={() => setConfirmingDisable(true)}
						className="shrink-0 rounded-full border border-destructive/30 px-4 py-2.5 text-sm font-semibold text-destructive transition hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-70"
					>
						{busy ? 'Disabling…' : 'Disable channel'}
					</button>
				</SoftCard>
			</div>

			<DisableChannelDialog
				channel={channel}
				open={confirmingDisable}
				onOpenChange={setConfirmingDisable}
				onConfirm={() => disable(channel.id).then(() => navigate(shopPath(`/channels/${channel.id}`)))}
			/>
		</div>
	);
}
